/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: RingGroupFormView.tsx (Ring Groups)
 🗓️ Created: 2025-01-15
 🧩 Description:
    Form view for creating and editing Ring Groups.
    Manages ring group configuration with members and advanced settings.

 🔧 Notes:
    - Refactored from monolithic 895-line file
    - Logic partially delegated to hooks/
    - API calls moved to api/
    - Complex settings and members logic kept in component

 🔁 Last Updated: 2025-01-15 by Rodrigo Cuadra (Refactored)
────────────────────────────────────────────────────────────────*/

import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Users, Search, Copy, Plus } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';
import { ModuleFormHeader } from '../../components/layouts';
import { FixedActionBar } from '../../components/ui/FixedActionBar';
import { useSaveWithAction } from '../../hooks/useSaveWithAction';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { QuickSearchModal } from '../../components/ui/QuickSearchModal';
import { RingGroupSection } from './RingGroupSection';
import { RingGroupMembersSection } from './RingGroupMembersSection';
import { RingGroupAdvancedSection } from './RingGroupAdvancedSection';
import { FormState, Strategy, MemberRow } from './types/ringGroups.types';
import { STRATEGY_OPTIONS } from './constants';
import { useRingGroupData } from './hooks/useRingGroupData';
import { useRingGroupForm } from './hooks/useRingGroupForm';
import { areMembersEqual } from './utils/memberHelpers';
import { fetchQuickListItems } from './api/ringGroupsApi';
import { navigateToRingGroupDuplicate } from './ringGroupsActions';

export default function RingGroupFormView() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const [searchParams] = useSearchParams();
  const duplicateId = searchParams.get('duplicate');
  const isEditMode = Boolean(id && id !== 'new');

  const { mappedData, isLoading } = useRingGroupData(id, duplicateId);

  const {
    formState,
    memberRows,
    extraSettings,
    errors,
    isSaving,
    hasChanges,
    handleFieldChange,
    handleMembersChange,
    handleExtraSettingsChange,
    handleSave,
    handleCancel
  } = useRingGroupForm(id, duplicateId, mappedData);

  const [presenceTouched, setPresenceTouched] = useState(false);
  const [showQuickList, setShowQuickList] = useState(false);
  const [quickListItems, setQuickListItems] = useState<Array<{ id: number; uuid?: string; name: string; description?: string | null }>>([]);
  const [quickListSearch, setQuickListSearch] = useState('');
  const [isQuickListLoading, setIsQuickListLoading] = useState(false);
  const quickListLoadingRef = useRef(false);
  const previousMemberRowsRef = useRef<MemberRow[]>([]);

  // Auto-generate context from name (for new items)
  useEffect(() => {
    if (isEditMode) {
      return;
    }

    const normalized =
      formState.name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/_{2,}/g, '_')
        .replace(/^_|_$/g, '')
        .substring(0, 128) || 'default';

    if (formState.context !== normalized) {
      handleFieldChange('context', normalized);
    }
  }, [formState.name, formState.context, isEditMode, handleFieldChange]);

  // Auto-generate presenceId from name and context (for new items)
  useEffect(() => {
    if (isEditMode || presenceTouched) {
      return;
    }

    const base =
      formState.name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/_{2,}/g, '_')
        .replace(/^_|_$/g, '')
        .substring(0, 128);

    const computed = base ? `${base}@${formState.context || 'default'}` : '';

    if (formState.presenceId !== computed) {
      handleFieldChange('presenceId', computed);
    }
  }, [formState.name, formState.context, formState.presenceId, presenceTouched, isEditMode, handleFieldChange]);

  const handleFieldChangeWithPresence = useCallback(
    <K extends keyof FormState>(field: K, value: FormState[K]) => {
      if (field === 'presenceId') {
        setPresenceTouched(true);
      }
      handleFieldChange(field, value);
    },
    [handleFieldChange]
  );

  const handleStrategyChange = useCallback((event: ChangeEvent<HTMLSelectElement>) => {
    const { value } = event.target;
    if (STRATEGY_OPTIONS.includes(value as Strategy)) {
      handleFieldChange('strategy', value as Strategy);
    }
  }, [handleFieldChange]);

  const handleFallbackModuleChange = useCallback((value: string) => {
    handleFieldChange('fallbackModule', value ? value : 'none');
    if (!value) {
      handleFieldChange('fallbackValue', '');
    }
  }, [handleFieldChange]);

  const handleFallbackValueChange = useCallback((value: string) => {
    handleFieldChange('fallbackValue', value);
  }, [handleFieldChange]);

  const handleDuplicateFromForm = useCallback(() => {
    if (!isEditMode || !id) return;
    navigateToRingGroupDuplicate(navigate, id);
  }, [id, isEditMode, navigate]);

  // Navigate to new
  const handleAddNew = useCallback(() => {
    navigate('/pbx/call-center/ring-groups/new');
  }, [navigate]);

  const loadQuickList = useCallback(async () => {
    if (quickListLoadingRef.current) {
      return;
    }

    quickListLoadingRef.current = true;
    setIsQuickListLoading(true);
    try {
      const items = await fetchQuickListItems(100);
      setQuickListItems(items);
    } catch {
      // Error handling is done in the hook
      setQuickListItems([]);
    } finally {
      setIsQuickListLoading(false);
      quickListLoadingRef.current = false;
    }
  }, []);

  const filteredQuickList = useMemo(() => {
    const query = quickListSearch.trim().toLowerCase();
    if (!query) {
      return quickListItems;
    }

    return quickListItems.filter((item) =>
      item.name.toLowerCase().includes(query) ||
      (item.description ?? '').toLowerCase().includes(query)
    );
  }, [quickListItems, quickListSearch]);

  // Enhanced save handler for useSaveWithAction
  const handleSaveInternal = useCallback(async () => {
    const savedData = await handleSave();

    // If no changes were detected, return current data to allow navigation
    if (!savedData && isEditMode && id) {
      // Return current data with ID/UUID for navigation purposes
      return { id, uuid: id };
    }

    return savedData;
  }, [handleSave, isEditMode, id]);

  // Use reusable hook for save actions
  const handleSaveWithAction = useSaveWithAction({
    onSave: handleSaveInternal,
    listPath: '/pbx/call-center/ring-groups',
    newPath: '/pbx/call-center/ring-groups/new',
    mode: isEditMode ? 'edit' : 'create',
    getEditPath: (savedData) => {
      // Ring Groups use UUID for navigation
      return `/pbx/call-center/ring-groups/${savedData.uuid || savedData.id}`;
    },
    getFormData: () => ({
      formState,
      memberRows,
      extraSettings
    }),
    storageKey: 'ringGroups:saveAndNewData'
  });

  // Memoize memberRows to prevent passing new reference when content hasn't changed
  const memoizedMemberRows = useMemo(() => {
    const previousRef = previousMemberRowsRef.current;
    const contentEqual = areMembersEqual(memberRows, previousRef);

    // If content is the same, return the previous reference to prevent child re-renders
    if (contentEqual) {
      return previousRef;
    }

    // Content changed, update the ref and return the new reference
    previousMemberRowsRef.current = memberRows;
    return memberRows;
  }, [memberRows]);

  return (
    <div className="p-6 pb-12 space-y-4">
      <ModuleFormHeader
        icon={<Users className="h-5 w-5 text-primary" />}
        title={isEditMode ? `${t('ringGroups.form.editTitle')} - ${formState.name || ''}` : t('ringGroups.form.createTitle')}
        backButtonText={t('common.list')}
        onBack={() => navigate(-1)}
        actions={[
          // 1. Quick Search (always visible)
          {
            icon: <Search className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('ringGroups.actions.quickList'),
            onClick: () => {
              setShowQuickList(true);
              void loadQuickList();
            },
          },
          // 2. Duplicate (edit mode only)
          ...(isEditMode
            ? [{
              icon: <Copy className="h-5 w-5 text-muted-foreground" />,
              tooltip: t('ringGroups.actions.duplicate'),
              onClick: handleDuplicateFromForm,
            }]
            : []),
          // 3. Add New (always visible)
          {
            icon: <Plus className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('ringGroups.actions.add'),
            onClick: handleAddNew,
          }
        ]}
      />

      <div className="space-y-4 pb-10">
        <Tabs defaultValue="main" className="space-y-4">
          <TabsList>
            <TabsTrigger value="main">{t('ringGroups.form.tabs.main')}</TabsTrigger>
            <TabsTrigger value="advanced">{t('ringGroups.form.tabs.advanced')}</TabsTrigger>
          </TabsList>

          <TabsContent value="main" className="space-y-4">
            <RingGroupSection
              formData={{
                name: formState.name,
                extension: formState.extension,
                strategy: formState.strategy,
                ringTimeout: formState.ringTimeout,
                fallbackModule: formState.fallbackModule,
                fallbackValue: formState.fallbackValue,
                callerIdName: formState.callerIdName,
                callerIdNumber: formState.callerIdNumber,
                greeting: formState.greeting,
                confirmKey: formState.confirmKey,
                confirmFile: formState.confirmFile,
                skipBusy: formState.skipBusy,
                enabled: formState.enabled
              }}
              onFieldChange={handleFieldChangeWithPresence}
              onStrategyChange={handleStrategyChange}
              onFallbackModuleChange={handleFallbackModuleChange}
              onFallbackValueChange={handleFallbackValueChange}
            />

            <RingGroupMembersSection
              members={memoizedMemberRows}
              ringTimeout={formState.ringTimeout}
              onChange={handleMembersChange}
            />
          </TabsContent>

          <TabsContent value="advanced" className="space-y-4">
            <RingGroupAdvancedSection
              formData={{
                distinctiveRing: formState.distinctiveRing,
                ringback: formState.ringback,
                callScreen: formState.callScreen,
                callForward: formState.callForward,
                followMe: formState.followMe,
                missedCallAction: formState.missedCallAction,
                forwardEnabled: formState.forwardEnabled,
                forwardNumber: formState.forwardNumber,
                forwardTollAllow: formState.forwardTollAllow,
                record: formState.record,
                callTimeoutOverride: formState.callTimeoutOverride,
                presenceId: formState.presenceId
              }}
              onFieldChange={handleFieldChangeWithPresence}
            />
          </TabsContent>
        </Tabs>

        <FixedActionBar
          onSaveWithAction={handleSaveWithAction}
          onCancel={handleCancel}
          isSaving={isSaving}
          isLoading={isLoading}
          isEditMode={isEditMode}
          hasChanges={hasChanges}
          saveText={t('common.save')}
          cancelText={t('common.cancel')}
          useSaveDropdown={true}
          saveDropdownStorageKey="ringGroups:saveActionPreference"
        />
      </div>

      <QuickSearchModal
        isOpen={showQuickList}
        onClose={() => {
          setShowQuickList(false);
          setQuickListSearch('');
          setIsQuickListLoading(false);
          quickListLoadingRef.current = false;
        }}
        title={t('ringGroups.quickList.title', 'Ring Groups')}
        items={filteredQuickList}
        isLoadingInitial={isQuickListLoading}
        isSearching={false}
        searchValue={quickListSearch}
        onSearchChange={setQuickListSearch}
        onItemSelect={(item) => {
          setShowQuickList(false);
          setQuickListSearch('');
          navigate(`/pbx/call-center/ring-groups/${item.uuid ?? item.id}`, { replace: true });
        }}
        currentItemId={id}
        renderItem={(item, isCurrent) => (
          <button
            type="button"
            disabled={isCurrent}
            onClick={() => {
              if (isCurrent) return;
              setShowQuickList(false);
              setQuickListSearch('');
              navigate(`/pbx/call-center/ring-groups/${item.uuid ?? item.id}`, { replace: true });
            }}
            className={`w-full text-left p-3 rounded-md border border-border transition-colors ${isCurrent
              ? 'bg-primary/20 border-2 border-primary cursor-default opacity-60'
              : 'hover:bg-muted/40'
              }`}
          >
            <div className="font-medium text-foreground">
              {item.name}
            </div>
            {item.description && (
              <div className="text-xs text-muted-foreground mt-0.5">
                {item.description}
              </div>
            )}
          </button>
        )}
        searchPlaceholder={t('ringGroups.quickList.searchPlaceholder', 'Search by name or description')}
        emptyMessage={t('ringGroups.quickList.empty', 'No ring groups found')}
        headerIcon={<Users className="h-5 w-5" />}
        minSearchChars={0}
      />
    </div>
  );
}
